import type { ClienteApi } from '../cliente';
import { claveIdempotencia, consulta, negocio } from '../comun';
import type { components, operations } from '../tipos/plataforma';

/**
 * Claves de API del negocio (13.6, G633): las cinco operaciones de Identidad y Acceso con `api.claves.gestionar`
 * (Propietario y Administrador). El secreto llega **solo** en la respuesta de crear y de rotar (`secret`).
 */
type Esquema<N extends keyof components['schemas']> = components['schemas'][N];

export type ClaveApi = Esquema<'ApiKey'>;
export type ClaveApiCreada = Esquema<'ApiKeyCreated'>;
export type AltaClaveApi = Esquema<'ApiKeyCreate'>;
export type ListaClavesApi = Esquema<'ApiKeyList'>;
export type UsoClaveApi = Esquema<'ApiKeyUsage'>;
export type VentanaUso = NonNullable<NonNullable<operations['getApiKeyUsage']['parameters']['query']>['window']>;
type ConsultaClaves = NonNullable<operations['listApiKeys']['parameters']['query']>;

export function operacionesClavesApi(api: ClienteApi) {
    const ruta = (id: string, resto: string): `/v1/${string}` => `${negocio(id)}/api-keys${resto}`;
    return {
        /** listApiKeys: las del negocio, sin el secreto. */
        listApiKeys: (businessId: string, c: ConsultaClaves = {}) => api.pedir<ListaClavesApi>('GET', ruta(businessId, consulta({ ...c }))),
        /** createApiKey: privada o de pruebas; el secreto, solo en esta respuesta. */
        createApiKey: (businessId: string, datos: AltaClaveApi, clave = claveIdempotencia()) =>
            api.pedir<ClaveApiCreada>('POST', ruta(businessId, ''), { cuerpo: datos, idempotencia: clave }),
        /** rotateApiKey: otra con los mismos permisos; la antigua sigue `overlap_hours` (0 a 168). */
        rotateApiKey: (businessId: string, id: string, horas: number, clave = claveIdempotencia()) =>
            api.pedir<ClaveApiCreada>('POST', ruta(businessId, `/${encodeURIComponent(id)}/rotate`), { cuerpo: { overlap_hours: horas }, idempotencia: clave }),
        /** revokeApiKey: deja de funcionar en el acto (también en solo lectura). */
        revokeApiKey: (businessId: string, id: string, clave = claveIdempotencia()) =>
            api.pedir<undefined>('DELETE', ruta(businessId, `/${encodeURIComponent(id)}`), { idempotencia: clave }),
        /** getApiKeyUsage: peticiones y frenadas por hora o por día. */
        getApiKeyUsage: (businessId: string, id: string, ventana: VentanaUso) =>
            api.pedir<UsoClaveApi>('GET', ruta(businessId, `/${encodeURIComponent(id)}/usage${consulta({ window: ventana })}`)),
    };
}
